import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as mediaDAO from '../../../dao/MediaDAO';
import type { Media, MediaSearchFilters } from '../types';

export const mediaListKey = (companyId: number) => ['media', companyId] as const;

const mediaQueryKey = (companyId: number, filters: MediaSearchFilters) =>
  [...mediaListKey(companyId), filters] as const;

export const useMediaList = (companyId: number, filters: MediaSearchFilters = {}, options: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const qKey = mediaQueryKey(companyId, filters);
  const enabled = options.enabled ?? true;

  // Scans add images in the background, so the library refetches whenever it is
  // opened rather than trusting a list from earlier in the session.
  const { data, isLoading, error, refetch } = useQuery<Media[]>({
    queryKey: qKey,
    queryFn: () => mediaDAO.search(companyId, filters).then((r: { list: Media[] }) => r.list ?? []),
    enabled,
    refetchOnMount: 'always',
  });

  const removeItem = useCallback((id: number) => {
    queryClient.setQueriesData<Media[]>(
      { queryKey: ['media'], exact: false },
      prev => prev?.filter(m => m.id !== id)
    );
  }, [queryClient]);

  const prependItem = useCallback((item: Media) => {
    queryClient.setQueriesData<Media[]>(
      { queryKey: mediaListKey(companyId), exact: false },
      prev => prev ? [item, ...prev.filter(m => m.id !== item.id)] : [item]
    );
    queryClient.invalidateQueries({ queryKey: ['media'], exact: false });
  }, [queryClient, companyId]);

  const updateItem = useCallback((item: Media) => {
    queryClient.setQueriesData<Media[]>(
      { queryKey: ['media'], exact: false },
      prev => prev ? prev.map(m => m.id === item.id ? item : m) : [item]
    );
  }, [queryClient]);

  return {
    list: data ?? [],
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch,
    removeItem,
    prependItem,
    updateItem,
  };
};
